import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, of, shareReplay, switchMap, throwError } from 'rxjs';

import { catchError, filter, finalize, map, take } from 'rxjs/operators';

import { AccountService } from '@core/services/account/account.service';
import { AppAccessService } from '@core/services/app-access.service';
import { IsLoadingService } from '@core/services/is-loading.service';
import {
  AUTH_UPDATE_SESSION_URL,
  createAnonymousAuthRejection,
  isAnonymousAuthBootstrapRequestUrl,
  isCurrentUserRequestUrl,
  isUpdateSessionRequestUrl,
} from '@core/helpers';
import type { AuthRefreshResult } from './auth-interceptor.types';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly isLoadingService$ = inject(IsLoadingService);
  private readonly accountService = inject(AccountService);
  private readonly appAccessService = inject(AppAccessService);
  private readonly http = inject(HttpClient);
  private ts = 0;
  private refreshRequest$: Observable<AuthRefreshResult> | null = null;
  private failedRefreshLogoutHandled = false;

  excludeUrlsToShowLoader = [
    '/api/outkeep/userSettings',
    '/installQuality/status', // чтобы интерфейс не передергивало при поллинге
  ];

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this.excludeUrlsToShowLoader.every(url => !req.url.includes(url))) {
      this.isLoadingService$.start();
    }

    return this.waitForAuth$(req).pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return throwError(() =>
            createAnonymousAuthRejection(req.urlWithParams),
          );
        }

        return next.handle(req).pipe(
          catchError(error => {
            if (
              error instanceof HttpErrorResponse &&
              error.status === 401 &&
              this.appAccessService.$accessMode() !== 'public' &&
              !isUpdateSessionRequestUrl(req.urlWithParams)
            ) {
              return this.handleUnauthorized(req, next, error);
            }

            return throwError(() => error);
          }),
        );
      }),
      finalize(() => this.isLoadingService$.stop()),
    );
  }

  private waitForAuth$(req: HttpRequest<unknown>): Observable<boolean> {
    // Auth-bootstrap запросы всегда проходят без ожидания
    if (
      isAnonymousAuthBootstrapRequestUrl(req.urlWithParams) ||
      this.appAccessService.$accessMode() === 'public'
    ) {
      return of(true);
    }

    const state = this.accountService.$authState();

    if (state === 'authenticated') {
      return of(true);
    }

    if (state === 'anonymous') {
      return of(false);
    }

    // state === 'unknown': ждём пока getSelf не разрешится
    return this.accountService.authState$.pipe(
      filter(s => s !== 'unknown'),
      take(1),
      map(s => s === 'authenticated'),
    );
  }

  private handleUnauthorized(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    return this.updateSession$().pipe(
      switchMap(refreshResult => {
        if (refreshResult.updated) {
          return next.handle(req);
        }

        if (this.shouldLogoutAfterFailedRefresh(req, refreshResult.error)) {
          this.logoutAfterFailedRefresh();
        }

        return throwError(() => error);
      }),
    );
  }

  private updateSession$(): Observable<AuthRefreshResult> {
    // если сессия обновилась менее 10 секунд назад, то просто повторяем запрос.
    if (Date.now() - this.ts < 10000) {
      return of({ updated: true } as const);
    }
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    this.failedRefreshLogoutHandled = false;
    this.refreshRequest$ = this.http
      .get(AUTH_UPDATE_SESSION_URL, {
        withCredentials: true,
      })
      .pipe(
        map(() => {
          this.ts = Date.now();

          return { updated: true } as const;
        }),
        catchError((error: unknown) => {
          return of({
            error: this.toHttpErrorResponse(error),
            updated: false,
          } as const);
        }),
        finalize(() => {
          this.refreshRequest$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshRequest$;
  }

  private logoutAfterFailedRefresh(): void {
    if (this.failedRefreshLogoutHandled) {
      return;
    }

    this.failedRefreshLogoutHandled = true;
    this.accountService.logout();
  }

  private shouldLogoutAfterFailedRefresh(
    req: HttpRequest<unknown>,
    error: HttpErrorResponse,
  ): boolean {
    // /logout в auth очищает cookies, поэтому дергаем его только при
    // подтвержденном отказе авторизации, а не при сетевых/серверных сбоях.
    return (
      !isCurrentUserRequestUrl(req.urlWithParams) &&
      (error.status === 401 || error.status === 403)
    );
  }

  private toHttpErrorResponse(error: unknown): HttpErrorResponse {
    if (error instanceof HttpErrorResponse) {
      return error;
    }

    return new HttpErrorResponse({ error });
  }
}
